"use client"

import { PageLayout } from "@/components/page-layout"

export default function DocumentationPage() {
  return (
    <PageLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Documentation</h1>
          <p className="text-muted-foreground">
            Introduction and Usage
          </p>
        </div>

        <div className="p-6">
          <h2 className="text-xl font-semibold text-card-foreground mb-4">Overview</h2>
          <p className="text-card-foreground leading-relaxed mb-4">
            Any delay in liquidation, caused by regulatory restraint (marketability), or lack of buyers at the current price (illiquidity) creates price risk for the holder. This price risk results in a lower price offered for stocks subject to a delayed liquidation. Larger blocks require longer periods to liquidate and are likely to incur significant blockage discounts.
          </p>
          <p className="text-card-foreground leading-relaxed mb-4">
            Optionmodeldlom provides a cloud-based DLOM utility for professionals who must quantify illiquidity in private interests and restricted securities—accurately, defensibly, and efficiently. Minimal resource commitment with no database download, no program installation, or maintenance.
          </p>
          <p className="text-card-foreground leading-relaxed mb-4">
            The platform combines real market empirical datasets, multi-model engines (Three option-pricing models), and automated, audit-ready documentation. It reduces cycle time for valuations by 40–70%, improves defensibility, and enables scalable adoption through role-based access and simple pricing.
          </p>
          <p className="text-card-foreground leading-relaxed">
            You have a choice of benchmarking Blockage and DLOM (Transfer Restrictions) discounts using peer group portfolios constructed across market value deciles, SIC code groups, and an intersection of industry and size portfolios. Alternatively, you can get granular ticker-based estimates for Blockage and DLOM discounts for individual market traded stocks.
          </p>
        </div>

        <div className="p-6">
          <h2 className="text-xl font-semibold text-card-foreground mb-4">Using the Calculator</h2>
          <p className="text-card-foreground leading-relaxed mb-4">
            Using the calculator is simple. Select the appropriate option:
          </p>
          
          <div className="space-y-4 mb-4">
            <div className="p-4 bg-muted rounded-lg">
              <h3 className="font-semibold text-foreground mb-2">DLOM & Blockage Calculator for benchmarking portfolio selection</h3>
              <ul className="list-disc list-inside space-y-1 text-card-foreground ml-4">
                <li>Enter year-month of analysis</li>
                <li>Market value estimate (Pre-discount)</li>
                <li>Block Size for the interest being valued</li>
                <li>SIC code (First two digits)</li>
                <li>Calculate Discount</li>
              </ul>
            </div>
            
            <div className="p-4 bg-muted rounded-lg">
              <h3 className="font-semibold text-foreground mb-2">Ticker-lookup for individual ticker</h3>
              <ul className="list-disc list-inside space-y-1 text-card-foreground ml-4">
                <li>Enter year-month of analysis</li>
                <li>Block Size for the interest being valued</li>
                <li>Calculate Discount</li>
              </ul>
            </div>
          </div>

          <p className="text-card-foreground leading-relaxed mb-4">
            Benchmarking portfolio results are presented for each of the exchange option models. Please select the appropriate model based on the buyer/seller combination.
          </p>
        </div>

        <div className="p-6">
          <h2 className="text-xl font-semibold text-card-foreground mb-4">Exchange Option Models</h2>
          <div className="space-y-4">
            <div className="p-4 bg-muted rounded-lg">
              <h3 className="font-semibold text-foreground mb-2">Margrabe Exchange Option</h3>
              <p className="text-card-foreground">
                Suitable for hypothetical buyer/seller case with both parties having all public information available. (Base case)
              </p>
            </div>
            
            <div className="p-4 bg-muted rounded-lg">
              <h3 className="font-semibold text-foreground mb-2">Asian Average Exchange Option</h3>
              <p className="text-card-foreground">
                Suitable for insider buyer/seller case with both parties having all public and privileged information available. (AKA Finnerty/Ghaidrov models)
              </p>
            </div>
            
            <div className="p-4 bg-muted rounded-lg">
              <h3 className="font-semibold text-foreground mb-2">Look Back Exchange Option</h3>
              <p className="text-card-foreground">
                Suitable for insider seller/outside buyer case with insider seller having all public and privileged information available, while the outsider buyer has access to only public information. (AKA Longstaff model)
              </p>
            </div>
          </div>
          
          <p className="text-card-foreground leading-relaxed mt-4">
            Results for each model are presented in a 3X3 matrix with nine values of estimated discounts, one for each of the volatility (low, median, high) estimates for each of the liquidity (low, median, high) estimates. Please select the appropriate combination of volatility and liquidity for the selected model.
          </p>
          
          <p className="text-card-foreground leading-relaxed mt-4">
            Ticker level discounts are based on applicable individual stock volatility and liquidity estimates for the ticker at the end of the specified year-month and the block size.
          </p>
        </div>

        <div className="p-6">
          <h2 className="text-xl font-semibold text-card-foreground mb-4">Theoretical Foundation</h2>
          <p className="text-card-foreground leading-relaxed">
            The exchange options-based models rest on well-established theory and use real market data. The results are transparent and replicable, making them defensible. Complete compliance/litigation work files can be generated with underlying data, model selection criteria, calculation steps, and results fully annotated.
          </p>
        </div>

        <div className="p-6">
          <h2 className="text-xl font-semibold text-card-foreground mb-4">Applicability Across Primary User Segments</h2>
          <p className="text-card-foreground leading-relaxed">
            Valuation professionals, accounting/tax professionals, and legal professionals.
          </p>
        </div>

        <div className="p-6">
          <h2 className="text-xl font-semibold text-card-foreground mb-4">Business Problem & Solution</h2>
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-foreground mb-2">Problem</h3>
              <p className="text-card-foreground leading-relaxed">
                DLOM estimation is fragmented, manual, and inconsistent. Firms lack centralized models, empirical benchmarks, and defensible documentation—leading to rework, regulatory risk, and delayed deal/tax timelines.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-foreground mb-2">Solution</h3>
              <p className="text-card-foreground leading-relaxed">
                A unified, cloud-native DLOM platform providing empirical grounding, model flexibility, scenario/sensitivity analysis, compliance-ready outputs, and collaboration—built for multi-stakeholder workflows (valuation, tax, audit, legal teams).
              </p>
            </div>
          </div>
        </div>

        <div className="p-6">
          <h2 className="text-xl font-semibold text-card-foreground mb-4">Core Value Proposition</h2>
          <ul className="list-disc list-inside space-y-2 text-card-foreground ml-4">
            <li><strong>Accuracy & Defensibility:</strong> Standardized methods, transparent assumptions, citations, and detailed audit trails.</li>
            <li><strong>Speed & Scalability:</strong> Templates, batch processing, and API integrations cut analysis time drastically.</li>
            <li><strong>Consistency Across Teams:</strong> Firm-wide templates and locked methodologies reduce variance and reviewer friction.</li>
            <li><strong>Compliance Alignment:</strong> Outputs aligned with USPAP/AICPA/ASC 820/IFRS 13 documentation expectations.</li>
            <li><strong>Collaboration:</strong> Cloud-based roles, sharing, and version control for multidisciplinary teams (valuation, tax, legal).</li>
          </ul>
        </div>

        <div className="p-6">
          <h2 className="text-xl font-semibold text-card-foreground mb-4">Feature Recommendations by Segment</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-3">1) Valuation Professionals (BV analysts, appraisers)</h3>
              <p className="text-card-foreground leading-relaxed mb-2"><strong>Jobs-to-be-done:</strong> Produce defensible DLOM estimates quickly; adhere to standards; withstand peer review and litigation.</p>
              <div className="mb-3">
                <h4 className="font-semibold text-foreground mb-2">Key Features</h4>
                <ul className="list-disc list-inside space-y-1 text-card-foreground ml-4">
                  <li><strong>Model Workbench:</strong> parameter transparency; peer-review mode.</li>
                  <li><strong>Empirical Explorer:</strong> Dataset filters (Industry, Market Value, Volatility, holding period).</li>
                  <li><strong>Report Builder:</strong> USPAP/AICPA-ready narratives with citations, tables, and appendices; version control.</li>
                  <li><strong>Template Governance:</strong> Firm-standard methods with admin locks; reviewer checklists.</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-foreground mb-2">KPIs</h4>
                <ul className="list-disc list-inside space-y-1 text-card-foreground ml-4">
                  <li>Time-to-complete per DLOM analysis ↓ 50–70%</li>
                  <li>Review cycles per report ↓ 30–50%</li>
                  <li>&quot;Accepted without challenge&quot; rate by audit/legal ↑</li>
                </ul>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-foreground mb-3">2) Accounting & Tax (CPAs, audit, estate/gift)</h3>
              <p className="text-card-foreground leading-relaxed mb-2"><strong>Jobs-to-be-done:</strong> Produce IRS/ASC 820/IFRS 13–defensible valuations; document thoroughly; pass audits.</p>
              <div className="mb-3">
                <h4 className="font-semibold text-foreground mb-2">Key Features</h4>
                <ul className="list-disc list-inside space-y-1 text-card-foreground ml-4">
                  <li><strong>Compliance Pack:</strong> IRS-aligned memo templates, ASC 820 fair value hierarchy tags, audit trails with signoffs.</li>
                  <li><strong>Tie-out Sheets:</strong> Auto-generated support schedules; Excel export with formulas.</li>
                  <li><strong>Reviewer Mode:</strong> Cross-referencing standards; exceptions dashboard.</li>
                  <li><strong>Evidence Locker:</strong> Immutable attachment storage (client docs, minutes, buy-sell terms).</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-foreground mb-2">KPIs</h4>
                <ul className="list-disc list-inside space-y-1 text-card-foreground ml-4">
                  <li>Audit comments specifically related to DLOM ↓ 40–60%</li>
                  <li>Rework hours per engagement ↓ 30–50%</li>
                  <li>Cycle time for estate/gift assignments ↓ 40%</li>
                </ul>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-foreground mb-3">3) Legal Professionals (litigation, disputes)</h3>
              <p className="text-card-foreground leading-relaxed mb-2"><strong>Jobs-to-be-done:</strong> Obtain credible, court-ready DLOMs; express assumptions clearly; meet tight deadlines.</p>
              <div className="mb-3">
                <h4 className="font-semibold text-foreground mb-2">Key Features</h4>
                <ul className="list-disc list-inside space-y-1 text-card-foreground ml-4">
                  <li><strong>Court-Ready Reports:</strong> Plain-language explanations; parameter defense scripts; cross-examination FAQs.</li>
                  <li><strong>Rapid Mode:</strong> Preconfigured templates for common case types (shareholder oppression, divorce, minority squeeze-outs).</li>
                  <li><strong>Chain of Custody:</strong> Evidence/version immutability; time-stamped logs; export to e-discovery formats.</li>
                  <li><strong>Expert Collaboration:</strong> Secure channels for counsel–expert commentary.</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-foreground mb-2">KPIs</h4>
                <ul className="list-disc list-inside space-y-1 text-card-foreground ml-4">
                  <li>Motions challenging DLOM methodology denied more frequently</li>
                  <li>Average prep time for testimony packages ↓ 40%</li>
                  <li>Positive judicial comments/acceptance citations ↑</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  )
}
